import React, { useState, useEffect, useCallback } from 'react';
import tw from 'twin.macro';
import { Button } from '../button';
import { Input, TextArea } from './FormParticals';
import { Icon } from '../icon';
import { UploadIcon, DumpsterIcon, TimesIcon, VideoIcon } from 'icons';
import { uploadFiles } from 'services/dumpert';
import { Loading } from '../loading';
import { Notification } from '../notifications';
import { gaEvent } from 'services/ga';
import { TextLine, Small } from '../text';

export const SUCCESS_MESSAGE = 'Je upload is succesvol verstuurd.';

const uploadDataInital = {
  name: '',
  email: '',
  title: '',
  description: '',
  accept: false,
  files: [],
};

export const errorMessages = {
  0: 'Er gaat iets mis... probeer het nog eens',
  400: `Er gaat iets mis... 
      Probeer je de juiste bestanden te uploaden? 
      Wij accepteren alleen plaatjes & video's.`,
  413: 'Er gaat iets mis... je bestand is te groot',
};

export const UploadForm = () => {
  const [dragOver, setDragOver] = useState(false);
  const [uploadData, setUploadData] = useState(uploadDataInital);
  const [submitState, setSubmitState] = useState(false);
  const [error, setError] = useState(undefined);
  const [result, setResult] = useState(undefined);
  const [progress, setProgress] = useState(0);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      gaEvent('dmp_upload', 'send');
      const formData = new FormData(e.target);
      formData.delete('files');
      if (uploadData.files) {
        const files = uploadData.files;
        for (let i = 0; i < files.length; i++) {
          formData.append(`file${i + 1}`, files[i].file);
        }
      }

      setError(undefined);
      setSubmitState('uploading');

      return uploadFiles(formData, setProgress)
        .then((response) => {
          switch (response.status) {
            case 400:
            case 413:
              gaEvent('dmp_upload', 'error_' + errorMessages[response.status]);
              setError({ message: errorMessages[response.status] });
              break;
            case 200:
              setResult(true);
              break;
            default:
              gaEvent('dmp_upload', 'error_' + errorMessages[0]);
              setError({ message: errorMessages[0] });
              break;
          }
        })
        .catch(() => {
          gaEvent('dmp_upload', 'error_' + errorMessages[0]);
          setError({ message: errorMessages[0] });
        })
        .finally(() => {
          gaEvent('dmp_upload', 'success');
          setSubmitState('done');
        });
    },
    [uploadData],
  );

  const filesHandleChange = (uploadFiles) => {
    if (uploadFiles) {
      const fileArray = [
        ...uploadData.files,
        ...uploadFiles.map((file) => {
          return {
            name: file.name,
            src: file.type.includes('video') ? 'video' : URL.createObjectURL(file),
            file,
          };
        }),
      ];

      const filterArray = (array) =>
        array.reduce((acc, current) => {
          if (!acc.some((item) => item.name === current.name)) {
            acc.push(current);
          }
          return acc;
        }, []);

      setUploadData({
        ...uploadData,
        files: [...filterArray(fileArray)],
      });
    }
  };

  const imageDelete = (deleteIndex) => {
    setUploadData({
      ...uploadData,
      files: [...uploadData.files.filter((a, index) => index !== deleteIndex)],
    });
  };

  useEffect(() => {
    if (result && submitState === 'done') {
      setUploadData(uploadDataInital);
    }
  }, [result, submitState]);

  return (
    <>
      {submitState === 'uploading' && (
        <>
          <div tw='w-full bg-input-back rounded-full h-2.5 mb-4'>
            <div tw='bg-brand h-2.5 rounded-full' style={{ width: `${progress}%` }}></div>
            <div tw='text-center text-brand font-bold'>{progress}%</div>
          </div>
          <div tw='h-1/2 flex justify-center items-center'>
            <Loading upload smallLoader showSuffix={false} />
          </div>
        </>
      )}
      {result && !error && (
        <Notification
          success
          message={SUCCESS_MESSAGE}
          onClick={() => {
            setResult(undefined);
            setSubmitState(false);
          }}
          tw='mb-4'
        />
      )}
      {error && <Notification danger message={error.message} onClick={() => setError(undefined)} />}
      <form
        id='uploadform'
        onSubmit={submit}
        encType='multipart/form-data'
        css={[submitState === 'uploading' && tw`hidden`]}
      >
        <input type='hidden' name='nr_of_files' value={uploadData.files.length} />
        <Input
          placeholder='Naam'
          name='user_name'
          id='user_name'
          value={uploadData.name}
          onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
          autoComplete='off'
          requiredField
          label={uploadData.name ? 'Naam' : ''}
        />
        <Input
          placeholder='E-mail'
          name='user_email'
          id='user_email'
          value={uploadData.email}
          type='email'
          onChange={(e) => setUploadData({ ...uploadData, email: e.target.value })}
          autoComplete='email'
          requiredField
          label={uploadData.email ? 'E-mail' : ''}
        />
        <Input
          placeholder='Titel'
          name='title'
          id='title'
          value={uploadData.title}
          onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
          autoComplete='off'
          requiredField
          label={uploadData.title ? 'Titel' : ''}
        />
        <TextArea
          placeholder='Beschrijving'
          name='uldescription'
          id='uldescription'
          rows='5'
          autoComplete='off'
          maxLength='500'
          value={uploadData.description}
          onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
          label={uploadData.description ? 'Beschrijving' : ''}
        />
        <Small tw='block -mt-2 pb-2'>{uploadData.description.length} / 500 karakters</Small>
        <div>
          <div
            css={[
              tw`rounded-lg bg-primary relative w-full h-36 border-2 border-icons border-dashed flex flex-col justify-center items-center mb-2`,
              dragOver && tw`bg-brand bg-opacity-30 border-brand`,
            ]}
          >
            <input
              name='files'
              type='file'
              placeholder='files'
              autoComplete='off'
              multiple
              onDragOver={(e) => {
                setDragOver(true);
                e.preventDefault();
                e.stopPropagation();
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={() => setDragOver(false)}
              onChange={(e) => {
                const uploadFiles = Array.from(e.target.files);
                filesHandleChange(uploadFiles);
              }}
              css={[dragOver && tw`bg-brand`]}
              required
              tw='h-full w-full inset-0 absolute opacity-0 cursor-pointer'
            />
            <Icon Type={dragOver ? DumpsterIcon : UploadIcon} aria-hidden='true' size='xlarge' />
            <span tw='line-clamp-1 font-bold'>
              {dragOver ? 'Bestanden hier dumpen' : 'Bestanden uploaden'}
            </span>
          </div>
          {uploadData.files.map((item, index) => (
            <div
              key={item.name}
              tw='bg-primary flex items-center justify-between mb-2 rounded-md px-2'
            >
              <div tw='w-16 h-16 flex justify-center items-center'>
                {item.src === 'video' ? (
                  <Icon Type={VideoIcon} large />
                ) : (
                  <img src={item.src} alt={item.name} tw='h-full w-full object-contain' />
                )}
              </div>
              <div tw='flex-grow px-2 text-left'>{item.name}</div>
              <Icon
                Type={TimesIcon}
                aria-hidden='true'
                noSpacing
                onClick={() => imageDelete(index)}
                tw='cursor-pointer hover:opacity-30'
              />
            </div>
          ))}
        </div>
        <div tw='py-2'>
          <input type='hidden' name='must_accept_conditions' value='1' />
          <Input
            type='checkbox'
            id='accept_conditions'
            name='accept_conditions'
            value='1'
            requiredField
            onChange={(e) => setUploadData({ ...uploadData, accept: e.target.checked })}
            checked={uploadData.accept}
          />
          <TextLine>
            Ja, ik accepteer de{' '}
            <a
              href='https://assets.dumpert.nl/algemene-gebruiksvoorwaarden-dtmedia.pdf'
              target='_blank'
              rel='noopener noreferrer'
              tw='text-brand'
            >
              algemene voorwaarden
            </a>
          </TextLine>
        </div>
        <div tw='flex justify-end'>
          <Button type='submit' label='Uploaden' primary />
        </div>
      </form>
    </>
  );
};
